import {
  highestSequence,
  mergeExperiment,
  type ResearchApi,
  type ResearchExperiment,
  type ResearchGetOptions,
  type ResearchRunOptions,
} from "./researchTypes";

export interface ResearchTransport {
  get(path: string): Promise<unknown>;
  post(path: string, body: Record<string, unknown>, signal?: AbortSignal): Promise<unknown>;
}

/** How often a background run is asked whether it is still `running`. */
export const RESEARCH_POLL_MS = 1000;

const aborted = () => new DOMException("The research run was stopped.", "AbortError");

/** A pause that ends early — and rejects like fetch does — when the signal aborts. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(aborted());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(aborted());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/** All calls use the existing authenticated transport, including cancellation of long runs. */
export function createKnowItResearch(transport: ResearchTransport): ResearchApi {
  const id = encodeURIComponent;
  const get = (runId: string, options?: ResearchGetOptions) =>
    transport.get(
      `research/experiments/${id(runId)}${
        options?.after === undefined ? "" : `?after=${encodeURIComponent(String(options.after))}`
      }`,
    ) as Promise<ResearchExperiment>;
  /**
   * A background start answers 202 with the experiment still `running`; the work continues
   * server-side. Poll until the status leaves `running` (`completed | paused | failed |
   * cancelled`) and hand back that final experiment. Each poll asks only for the events after
   * the highest sequence held (plus the live ones) and merges them in; `onUpdate` sees every
   * merged step. Aborting the signal only stops THIS polling — the caller asks the server to
   * stop with `cancel`.
   */
  const settle = async (
    runId: string,
    started: Promise<unknown>,
    signal: AbortSignal,
    onUpdate?: ResearchRunOptions["onUpdate"],
  ): Promise<ResearchExperiment> => {
    let current = (await started) as ResearchExperiment;
    onUpdate?.(current);
    while (current.status === "running") {
      await pause(RESEARCH_POLL_MS, signal);
      current = mergeExperiment(current, await get(runId, { after: highestSequence(current) }));
      onUpdate?.(current);
    }
    return current;
  };
  const start = (
    runId: string,
    action: "run" | "continue",
    body: Record<string, unknown>,
    signal: AbortSignal,
    options: ResearchRunOptions | undefined,
  ) => {
    const path = `research/experiments/${id(runId)}/${action}`;
    return options?.background
      ? settle(
          runId,
          transport.post(`${path}?background=true`, body, signal),
          signal,
          options.onUpdate,
        )
      : (transport.post(path, body, signal) as Promise<ResearchExperiment>);
  };
  return {
    recover: (runId, recoveryId) =>
      transport.post(`research/experiments/${id(runId)}/recover`, { id: recoveryId }) as ReturnType<
        ResearchApi["recover"]
      >,
    flow: (runId) =>
      transport.get(`research/experiments/${id(runId)}/flow`) as ReturnType<ResearchApi["flow"]>,
    models: () => transport.get("research/models") as ReturnType<ResearchApi["models"]>,
    download: (alias, repoId, revision = "main") =>
      transport.post("research/models/download", { id: alias, repoId, revision }) as ReturnType<
        ResearchApi["download"]
      >,
    cancelDownload: (alias) => transport.post(`research/models/${id(alias)}/cancel`, {}),
    bind: (modelId, thinkerId, displayName, role) =>
      transport.post("research/thinkers", { modelId, thinkerId, displayName, role }) as ReturnType<
        ResearchApi["bind"]
      >,
    impact: (request) =>
      transport.post("research/impact", { ...request }) as ReturnType<ResearchApi["impact"]>,
    context: (request) =>
      transport.post("research/context", { ...request }) as ReturnType<ResearchApi["context"]>,
    list: (options) =>
      transport.get(
        options
          ? `research/experiments?offset=${options.offset ?? 0}&query=${encodeURIComponent(options.query ?? "")}`
          : "research/experiments",
      ) as ReturnType<ResearchApi["list"]>,
    saveNotes: (runId, text, version) =>
      transport.post(`research/experiments/${id(runId)}/notes`, { text, version }) as ReturnType<
        ResearchApi["saveNotes"]
      >,
    create: (runId, request) =>
      transport.post("research/experiments", { id: runId, request }) as ReturnType<
        ResearchApi["create"]
      >,
    get,
    run: (runId, signal, options) => start(runId, "run", {}, signal, options),
    cancel: (runId) =>
      transport.post(`research/experiments/${id(runId)}/cancel`, {}) as ReturnType<
        ResearchApi["cancel"]
      >,
    // Always a background round: 202 now, then the same polling as a background continue.
    say: (runId, text, to, signal, options) =>
      settle(
        runId,
        transport.post(
          `research/experiments/${id(runId)}/say`,
          { text, to, ...(options?.rounds === undefined ? {} : { rounds: options.rounds }) },
          signal,
        ),
        signal,
        options?.onUpdate,
      ),
    roles: () => transport.get("research/roles") as ReturnType<ResearchApi["roles"]>,
    councils: () => transport.get("research/councils") as ReturnType<ResearchApi["councils"]>,
    ask: (runId, text, to) =>
      transport.post(`research/experiments/${id(runId)}/questions`, { text, to }) as ReturnType<
        ResearchApi["ask"]
      >,
    continueRounds: (runId, input, signal, options) =>
      start(
        runId,
        "continue",
        {
          ...(input.rounds === undefined ? {} : { rounds: input.rounds }),
          ...(input.steer === undefined ? {} : { steer: input.steer }),
        },
        signal,
        options,
      ),
  };
}
